import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { duration, ease } from '../lib/motion'

// Best-guess autocomplete tokens keyed by the step ids used across every
// form built on StepForm (InquiryForm, FreeBuildForm) — Chrome/Safari flag
// text inputs with no autocomplete hint, and this covers every id in use
// today. Unrecognised ids fall back to 'off' rather than guessing wrong.
const AUTOCOMPLETE_BY_STEP_ID = {
  name: 'name',
  email: 'email',
  contact: 'email',
  phone: 'tel',
  business: 'organization',
  social: 'url',
  currentSite: 'url',
}

/**
 * Reusable multi-step form engine, styled after SideShift's onboarding
 * flow: a thin progress bar that fills as you advance, one question per
 * screen, a pill-shaped input or set of pill choices, and a Back/Continue
 * footer pinned under the card.
 *
 * Deliberately dumb about submission — it just collects `values` across
 * steps and calls `onComplete(values)` on the final step. The caller owns
 * the actual Netlify fetch/redirect, same pattern every form on this site
 * already uses (see IntakeForm.jsx / PartnerApplicationForm.jsx).
 *
 * step shape:
 *   {
 *     id: string                 // unique, used as the field name
 *     title: string               // big question text
 *     subtitle?: string
 *     type: 'text' | 'email' | 'textarea' | 'choice' | 'checkbox'
 *     placeholder?: string
 *     required?: boolean
 *     options?: Array<string | { label: string, description?: string }>  // for type: 'choice'
 *     helperLink?: { label: string, onClick: () => void }  // optional escape
 *                                                           // hatch shown under this step only
 *   }
 */
export default function StepForm({ steps, onComplete, submitLabel = 'Send it', sending = false }) {
  const [index, setIndex] = useState(0)
  const [direction, setDirection] = useState(1)
  const [values, setValues] = useState({})
  const [error, setError] = useState('')
  const inputRef = useRef(null)

  const step = steps[index]
  const isLast = index === steps.length - 1
  const progress = ((index + 1) / steps.length) * 100

  useEffect(() => {
    if ((step.type === 'text' || step.type === 'email' || step.type === 'textarea') && inputRef.current) {
      inputRef.current.focus()
    }
  }, [index, step.type])

  const currentValue = values[step.id] ?? (step.type === 'checkbox' ? false : '')

  function setValue(v) {
    setValues((prev) => ({ ...prev, [step.id]: v }))
    setError('')
  }

  function validate() {
    if (step.type === 'checkbox') return true
    if (step.required && !String(currentValue).trim()) {
      setError("This one's required to continue.")
      return false
    }
    if (step.type === 'email' && String(currentValue).trim()) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailPattern.test(String(currentValue).trim())) {
        setError("That doesn't look like a valid email.")
        return false
      }
    }
    return true
  }

  function goNext() {
    if (!validate()) return
    if (isLast) {
      onComplete(values)
      return
    }
    setDirection(1)
    setIndex((i) => i + 1)
  }

  function goBack() {
    if (index === 0) return
    setError('')
    setDirection(-1)
    setIndex((i) => i - 1)
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && step.type !== 'textarea' && step.type !== 'choice') {
      e.preventDefault()
      goNext()
    }
  }

  return (
    <div
      className="rounded-2xl border overflow-hidden"
      style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
    >
      <div style={{ height: 6, width: '100%', background: 'var(--border)' }}>
        <motion.div
          animate={{ width: `${progress}%` }}
          transition={{ duration: duration.fast, ease: ease.out }}
          style={{ height: '100%', background: 'var(--accent)' }}
        />
      </div>

      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={step.id}
          custom={direction}
          initial={{ opacity: 0, x: direction === 1 ? 24 : -24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction === 1 ? -24 : 24 }}
          transition={{ duration: duration.fast, ease: ease.out }}
          style={{ padding: '36px 32px 28px' }}
        >
          <p
            className="text-xs mb-2"
            style={{ color: 'var(--text-muted)' }}
          >
            Step {index + 1} of {steps.length}
          </p>
          <h3
            id={`step-label-${step.id}`}
            className="font-display text-xl md:text-2xl mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            {step.title}
            {step.required && step.type !== 'checkbox' && (
              <span style={{ color: 'var(--accent)' }}> *</span>
            )}
          </h3>
          {step.subtitle && (
            <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
              {step.subtitle}
            </p>
          )}
          {!step.subtitle && <div className="mb-6" />}

          {step.type === 'choice' ? (
            <div className="flex flex-col gap-2.5">
              {step.options.map((option) => {
                const label = typeof option === 'string' ? option : option.label
                const description = typeof option === 'string' ? null : option.description
                const active = currentValue === label
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setValue(label)}
                    className="w-full text-left rounded-2xl border px-5 py-3.5 transition-colors"
                    style={{
                      background: active ? 'var(--accent)' : 'var(--bg-elevated)',
                      color: active ? 'var(--accent-text)' : 'var(--text-primary)',
                      borderColor: active ? 'var(--accent)' : 'var(--border)',
                    }}
                  >
                    <span className="block text-sm font-medium">{label}</span>
                    {description && (
                      <span
                        className="block text-xs mt-1 leading-snug"
                        style={{ color: active ? 'var(--accent-text)' : 'var(--text-secondary)', opacity: active ? 0.9 : 1 }}
                      >
                        {description}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          ) : step.type === 'checkbox' ? (
            <label
              className="flex items-start gap-3 text-sm rounded-2xl border px-5 py-4 cursor-pointer"
              style={{ borderColor: 'var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}
            >
              <input
                type="checkbox"
                id={step.id}
                name={step.id}
                checked={Boolean(currentValue)}
                onChange={(e) => setValue(e.target.checked)}
                className="mt-1"
              />
              <span>{step.checkboxLabel || step.title}</span>
            </label>
          ) : step.type === 'textarea' ? (
            <textarea
              ref={inputRef}
              id={step.id}
              name={step.id}
              aria-labelledby={`step-label-${step.id}`}
              value={currentValue}
              placeholder={step.placeholder}
              rows={4}
              onChange={(e) => setValue(e.target.value)}
              className="w-full rounded-2xl border px-5 py-4 text-sm focus-visible:outline-none resize-none"
              style={{ background: 'var(--bg-elevated)', borderColor: 'var(--border)', color: 'var(--text-primary)' }}
            />
          ) : (
            <input
              ref={inputRef}
              id={step.id}
              name={step.id}
              aria-labelledby={`step-label-${step.id}`}
              autoComplete={AUTOCOMPLETE_BY_STEP_ID[step.id] || 'off'}
              type={step.type === 'email' ? 'email' : 'text'}
              value={currentValue}
              placeholder={step.placeholder}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full rounded-full border px-5 py-3.5 text-sm focus-visible:outline-none"
              style={{ background: 'var(--bg-elevated)', borderColor: 'var(--border)', color: 'var(--text-primary)' }}
            />
          )}

          {error && (
            <p className="text-xs mt-3" style={{ color: 'var(--accent)' }}>
              {error}
            </p>
          )}

          {step.helperLink && (
            <button
              type="button"
              onClick={step.helperLink.onClick}
              className="mt-4 text-xs underline underline-offset-4"
              style={{ color: 'var(--text-muted)' }}
            >
              {step.helperLink.label}
            </button>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex gap-2.5" style={{ padding: '0 32px 28px' }}>
        <button
          type="button"
          onClick={goBack}
          disabled={index === 0}
          className="rounded-full border px-5 py-3.5 text-sm font-medium"
          style={{
            background: 'transparent',
            color: 'var(--text-primary)',
            borderColor: 'var(--border-strong)',
            opacity: index === 0 ? 0.35 : 1,
            cursor: index === 0 ? 'default' : 'pointer',
          }}
        >
          Back
        </button>
        <button
          type="button"
          onClick={goNext}
          disabled={sending}
          className="flex-1 rounded-full px-5 py-3.5 text-sm font-medium"
          style={{
            background: 'var(--accent)',
            color: 'var(--accent-text)',
            opacity: sending ? 0.7 : 1,
            cursor: sending ? 'default' : 'pointer',
          }}
        >
          {isLast ? (sending ? 'Sending…' : submitLabel) : 'Continue'}
        </button>
      </div>
    </div>
  )
}
